import React, { useEffect, useMemo, useState } from "react";
import "./admin-card-rates.css";
import { HashLoader } from "react-spinners";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import { getCardRatesForAdmin, updateCardRatesForAdmin } from "../../api/payment.api";

export const AdminCardRates = () => {
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [ratesPayload, setRatesPayload] = useState(null);
  const [editingRates, setEditingRates] = useState({});

  const rates = ratesPayload?.data || [];
  const source = ratesPayload?.source || "static";
  const generatedAt = ratesPayload?.generatedAt;
  const cacheExpiresAt = ratesPayload?.cacheExpiresAt;

  useEffect(() => {
    document.title = "Admin - Tỷ lệ thẻ cào";
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const res = await getCardRatesForAdmin();
      if (res.success) {
        setRatesPayload(res.data || null);
        setEditingRates({});
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const getRowKey = (cardType, price) => `${Number(cardType)}:${Number(price || 0)}`;

  const getEditableRate = (cardType, cardName, price, currentRate) => {
    const key = getRowKey(cardType, price);
    const editingValue = editingRates[key];
    if (editingValue === undefined) return Number(currentRate || 0);
    return editingValue;
  };

  const onChangeRate = (cardType, price, value) => {
    const key = getRowKey(cardType, price);
    setEditingRates((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onSaveRate = async ({ cardType, cardName, price, currentRate }) => {
    const key = getRowKey(cardType, price);
    const nextRate = Number(editingRates[key] ?? currentRate);
    if (!Number.isFinite(nextRate) || nextRate < 0 || nextRate > 100) {
      showToast("Rate phải từ 0 đến 100", "error");
      return;
    }

    try {
      setSavingKey(key);
      const res = await updateCardRatesForAdmin([
        {
          cardType,
          cardName,
          price: Number(price || 0),
          rate: nextRate,
        },
      ]);

      if (res.success) {
        showToast("Cập nhật rate thành công", "success");
        await fetchRates();
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setSavingKey("");
    }
  };

  const summary = useMemo(() => {
    let totalPrices = 0;
    let avgRate = 0;

    rates.forEach((card) => {
      totalPrices += card?.prices?.length || 0;
      avgRate += Number(card?.defaultFinalRate || 0);
    });

    const safeAvg = rates.length > 0 ? avgRate / rates.length : 0;
    return {
      totalCards: rates.length,
      totalPrices,
      avgRate: safeAvg,
    };
  }, [rates]);

  return (
    <div className="admin-card-rates-page">
      <ToastNotification />

      <div className="admin-card-rates-shell">
        <div className="admin-card-rates-head">
          <div>
            <p className="admin-card-rates-breadcrumb">Quản trị / Tỷ lệ thẻ cào</p>
            <h1 className="admin-card-rates-title">Card Rate Dashboard</h1>
          </div>
          <button className="admin-card-rates-refresh-btn" onClick={fetchRates} disabled={loading} type="button">
            Làm mới
          </button>
        </div>

        <div className="admin-card-rates-kpi">
          <div className="admin-card-rates-kpi-item">
            <span>Loại thẻ</span>
            <strong>{summary.totalCards}</strong>
          </div>
          <div className="admin-card-rates-kpi-item">
            <span>Mệnh giá đang bật</span>
            <strong>{summary.totalPrices}</strong>
          </div>
          <div className="admin-card-rates-kpi-item">
            <span>Rate TB</span>
            <strong>{summary.avgRate.toFixed(2)}%</strong>
          </div>
          <div className="admin-card-rates-kpi-item">
            <span>Nguồn dữ liệu</span>
            <strong>{source === "dynamic" ? "Doithegiatot.com" : "Mặc định"}</strong>
          </div>
        </div>

        <div className="admin-card-rates-meta">
          <span>Cập nhật: {generatedAt ? new Date(generatedAt).toLocaleString("vi-VN") : "-"}</span>
          <span>Cache hết hạn: {cacheExpiresAt ? new Date(cacheExpiresAt).toLocaleString("vi-VN") : "-"}</span>
        </div>

        {loading && rates.length === 0 ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <div className="admin-card-rates-table-wrap">
            <table className="admin-card-rates-table">
              <thead>
                <tr>
                  <th>Card type</th>
                  <th>Tên thẻ</th>
                  <th>Rate mặc định</th>
                  <th>Mệnh giá</th>
                  <th>Rate base</th>
                  <th>Rate áp dụng</th>
                  <th>Cập nhật</th>
                </tr>
              </thead>
              <tbody>
                {rates.length > 0 ? (
                  rates.flatMap((card) => {
                    const prices = card?.prices?.length ? card.prices : [{ price: "-", baseRate: card.baseRate, finalRate: card.defaultFinalRate, globalExtra: card.globalExtra, cardExtra: card.cardExtra, priceExtra: 0 }];
                    return prices.map((priceItem, index) => {
                      const rowKey = getRowKey(card.cardType, priceItem.price === "-" ? 0 : Number(priceItem.price || 0));
                      const editableRate = getEditableRate(
                        card.cardType,
                        card.cardName,
                        priceItem.price === "-" ? 0 : Number(priceItem.price || 0),
                        priceItem.finalRate
                      );
                      return (
                      <tr key={`${card.cardType}-${String(priceItem.price)}-${index}`}>
                        <td>{card.cardType}</td>
                        <td className="admin-card-rates-cell-name">{card.cardName}</td>
                        <td>{Number(card.defaultFinalRate || 0).toFixed(2)}%</td>
                        <td>{priceItem.price === "-" ? "-" : Number(priceItem.price || 0).toLocaleString("vi-VN")} {priceItem.price === "-" ? "" : "đ"}</td>
                        <td>{Number(priceItem.baseRate || 0).toFixed(2)}%</td>
                        <td className="admin-card-rates-cell-rate">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            className="admin-card-rates-rate-input"
                            value={editableRate}
                            onChange={(e) => onChangeRate(card.cardType, priceItem.price === "-" ? 0 : Number(priceItem.price || 0), e.target.value)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="admin-card-rates-save-btn"
                            onClick={() =>
                              onSaveRate({
                                cardType: card.cardType,
                                cardName: card.cardName,
                                price: priceItem.price === "-" ? 0 : Number(priceItem.price || 0),
                                currentRate: priceItem.finalRate,
                              })
                            }
                            disabled={savingKey === rowKey}
                          >
                            {savingKey === rowKey ? "Đang lưu..." : "Lưu"}
                          </button>
                        </td>
                      </tr>
                    )});
                  })
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <p className="admin-card-rates-empty">Không có dữ liệu rate.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
